import { Button } from '@/components/ui/button';
import type { Locale } from '@/config/locales';
import type { MembershipRewardPreview } from '@/lib/api/split-payment';

interface Props {
  preview: MembershipRewardPreview | null | undefined;
  isLoggedIn: boolean;
  locale: Locale;
  onJoin: () => void;
}

const copy = {
  es: {
    title: (points: number) => `Este pago equivale a aproximadamente ${points.toLocaleString()} puntos`,
    body: (rate: string) => `Crea tu cuenta gratis y acumula puntos a partir de tu próxima compra (${rate}% de acumulación).`,
    noRetroactive: 'Este pago no se puede acreditar de forma retroactiva.',
    action: 'Crear cuenta',
  },
  ko: {
    title: (points: number) => `이 결제 금액은 예상 약 ${points.toLocaleString()}포인트에 해당해요`,
    body: (rate: string) => `무료로 가입하고 다음 주문부터 포인트를 적립하세요(적립률 ${rate}%).`,
    noRetroactive: '이번 결제는 가입 후 소급 적립되지 않습니다.',
    action: '가입하기',
  },
  en: {
    title: (points: number) => `This payment is worth approximately ${points.toLocaleString()} points`,
    body: (rate: string) => `Create a free account and earn points starting with your next order (${rate}% earn rate).`,
    noRetroactive: 'This payment cannot be credited retroactively.',
    action: 'Create account',
  },
};

export function GuestPointsIncentive({ preview, isLoggedIn, locale, onJoin }: Props) {
  if (isLoggedIn || preview === null || preview === undefined || preview.missed_points <= 0) {
    return null;
  }

  const t = locale === 'es' ? copy.es : locale === 'ko' ? copy.ko : copy.en;
  const earnRatePercent = (preview.earn_rate * 100).toLocaleString(locale, {
    maximumFractionDigits: 2,
  });

  return (
    <section
      className="w-full max-w-md rounded-lg border border-primary/20 bg-primary/5 p-4"
      data-testid="guest-points-incentive"
    >
      <h3 className="text-sm font-semibold text-foreground">
        {t.title(preview.missed_points)}
      </h3>
      <p className="mt-1 text-xs leading-5 text-muted-foreground">
        {t.body(earnRatePercent)}
      </p>
      {preview.retroactive_credit_available === false ? (
        <p className="mt-1 text-xs leading-5 text-muted-foreground">
          {t.noRetroactive}
        </p>
      ) : null}
      <Button type="button" onClick={onJoin} className="mt-3 w-full">
        {t.action}
      </Button>
    </section>
  );
}
